import { useContext, useEffect, useRef, useState } from 'react';
import { AxiosResponse } from 'axios';

import { getCompanyWorkersfRequest } from '@vyce/core/src/api/companies';
import { GetCompanyWorkersResponse, ListCompanyWorkersRequest } from '@vyce/core/src/api/types';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { CompanyWorkerItem } from '@vyce/core/src/types';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';

const PAGE_SIZE = 50;
const SEARCH_DELAY = 300;

export type CompanyWorkersRequest = (
  companyId: string,
  payload: ListCompanyWorkersRequest
) => Promise<AxiosResponse<GetCompanyWorkersResponse>>;

interface Options {
  companyId: string;
  /** Module display names as the API spells them: 'Teams' | 'Time&Attendance' | 'Payroll' */
  modules?: string[];
  /** Team / site / pay schedule ids — narrows the list to members of those resources */
  resourceIds?: string[];
  /**
   * Per-module onboarding status on each row (what User Management shows). It is an extra
   * server-side lookup, so a picker that never renders it should pass false.
   */
  includeOnboarding?: boolean;
  /**
   * Which route lists the workers. Defaults to the permission-aware companies route; the invite
   * dialog passes the invite-scoped one that returns every worker of the company.
   */
  request?: CompanyWorkersRequest;
}

/**
 * Searchable, paginated company workers list for pickers (Forms recipients, invites).
 * Search is debounced; a filter change restarts from page 0 keeping the current search text.
 */
export const useCompanyWorkersList = ({
  companyId,
  modules,
  resourceIds,
  includeOnboarding = true,
  request = getCompanyWorkersfRequest,
}: Options) => {
  const [search, setSearch] = useState<string>('');
  const [workers, setWorkers] = useState<CompanyWorkerItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const { handleServerError } = useContext(NotificationContext);
  const debouncedSearch = useDebounceValue(search, SEARCH_DELAY);
  // The workers request has no abort support, so ignore responses of outdated requests
  const requestIdRef = useRef(0);
  const lastSubstringRef = useRef<string | null>(null);
  // Callers pass fresh arrays on every render — compare filters by value, not identity
  const filterKey = JSON.stringify([modules ?? null, resourceIds ?? null]);

  const fetchWorkers = async (offset: number, substring: string) => {
    const requestId = ++requestIdRef.current;
    lastSubstringRef.current = substring;
    try {
      if (offset === 0) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const { data } = await request(companyId, {
        offset,
        limit: PAGE_SIZE,
        substring,
        order_by: [{ field_name: 'first_name', desc: false }],
        ...(modules?.length ? { modules } : {}),
        ...(resourceIds?.length ? { resource_ids: resourceIds } : {}),
        include_onboarding: includeOnboarding,
      });
      if (requestId !== requestIdRef.current) {
        return;
      }
      setTotal(data.count);
      setWorkers(value => (offset === 0 ? data.items : [...value, ...data.items]));
    } catch (error) {
      if (requestId === requestIdRef.current) {
        handleServerError(error);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const loadMore = () => {
    if (loading || loadingMore || workers.length >= total) {
      return;
    }
    fetchWorkers(workers.length, lastSubstringRef.current ?? '');
  };

  useEffect(() => {
    if (!companyId) {
      // No company yet (Legend before a company is picked) — show an empty, settled list
      requestIdRef.current += 1;
      setWorkers([]);
      setTotal(0);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    fetchWorkers(0, lastSubstringRef.current ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, filterKey]);

  useEffect(() => {
    // useDebounceValue is null until the first delay elapses — that tick must not refetch
    if (!companyId || debouncedSearch === null || debouncedSearch === lastSubstringRef.current) {
      return;
    }
    fetchWorkers(0, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return {
    search,
    setSearch,
    workers,
    total,
    loading,
    loadingMore,
    hasMore: workers.length < total,
    loadMore,
  };
};
